<template>
  <!-- 맵 없음 placeholder -->
  <div v-if="!map" class="amx-empty">
    <div class="amx-empty-icon">🗺️</div>
    <p class="amx-empty-text">{{ $t('assetMapSolum.noFloorplan') }}</p>
    <router-link v-if="!mini" to="/assetMap" class="amx-empty-link">{{ $t('assetMapSolum.gotoAssetMap') }}</router-link>
  </div>

  <div v-else class="amx" :class="{ 'amx-mini': mini, 'amx-editing': isEditing }">
    <!-- 상단 헤더: canEdit (편집 가능) 이거나 isDirty 일 때만 표시.
         보기-only 사용처(대시보드 등)에서는 부모가 이미 floor 이름을
         보여주므로 중복 방지를 위해 숨김. -->
    <div v-if="!mini && (canEdit || isDirty)" class="amx-header">
      <div class="amx-title">
        <span v-if="isDirty" class="amx-dirty">{{ $t('assetMapSolum.unsavedChanges') }}</span>
      </div>
      <div v-if="canEdit" class="amx-actions">
        <button class="amx-btn" :class="{ 'is-on': isEditing }" @click="toggleEdit">
          {{ isEditing ? $t('assetMapSolum.viewMode') : $t('assetMapSolum.editMode') }}
        </button>
        <button v-if="isEditing" class="amx-btn amx-btn-primary" :disabled="saving || !isDirty" @click="save">
          {{ saving ? $t('assetMapSolum.saving') : $t('assetMapSolum.save') }}
        </button>
      </div>
    </div>

    <!-- 본문: 지도 + 사이드바 -->
    <div class="amx-body">
      <!-- 지도 영역 -->
      <div ref="frameEl" class="amx-map-frame">
        <div
          ref="containerEl"
          class="amx-canvas"
          :style="canvasStyle"
          @dragover.prevent="onDragOver"
          @drop.prevent="onDrop"
          @click.self="selectedPlacement = null"
        >
          <img :src="map.image_path" :alt="map.name" class="amx-img" draggable="false" />

          <div
            v-for="p in placements"
            :key="p.id"
            class="amx-placement"
            :class="[
              `amx-color-${p.color || 'emerald'}`,
              { 'amx-placement-connected': isConnected(p.gw_mac) },
              { 'amx-placement-selected': selectedPlacement && selectedPlacement.id === p.id },
            ]"
            :style="placementStyle(p)"
            :title="gwTitle(p.gw_mac)"
            @mousedown="onPlacementMouseDown($event, p)"
            @click.stop="onPlacementClick(p)"
          >
            <div class="amx-placement-label">{{ gwName(p.gw_mac) }}</div>
            <div class="amx-placement-count">{{ tagCount(p.gw_mac) }}</div>
            <div class="amx-placement-status" :class="isConnected(p.gw_mac) ? 'on' : 'off'"></div>
            <!-- 게이트웨이 온습도 센서(0x0C) 습도 — 정상·10분 이내 측정값만. 온도는 보드 발열 보정(챔버 캘리브레이션) 후 표시 예정 -->
            <div v-if="gwHumidity(p.gw_mac) !== null" class="amx-placement-hum">
              <span class="material-symbols-outlined amx-hum-icon">humidity_percentage</span>{{ gwHumidity(p.gw_mac).toFixed(0) }}%
            </div>

            <button
              v-if="isEditing"
              class="amx-placement-remove"
              :title="$t('assetMapSolum.delete')"
              @click.stop="removePlacement(p)"
              @mousedown.stop
            >×</button>

            <div
              v-if="isEditing"
              class="amx-placement-resize"
              :title="$t('assetMapSolum.resize')"
              @mousedown.stop="startResize($event, p)"
            ></div>

            <!-- 색 선택 (편집 모드, hover) -->
            <div v-if="isEditing" class="amx-color-picker" @mousedown.stop>
              <button
                v-for="c in availableColors"
                :key="c"
                class="amx-color-dot"
                :class="[`amx-color-bg-${c}`, { 'is-active': (p.color || 'emerald') === c }]"
                :title="c"
                @click.stop="p.color = c; isDirty = true"
              ></button>
            </div>
          </div>
        </div>
      </div>

      <!-- 사이드바: 편집 모드 = 사용 가능한 GW 목록 / 보기 모드 = 선택된 GW 의 태그 목록 -->
      <aside v-if="!mini && isEditing" class="amx-sidebar">
        <h3 class="amx-side-title">{{ $t('assetMapSolum.availableGateways', { n: availableGateways.length }) }}</h3>
        <p v-if="availableGateways.length === 0" class="amx-side-hint">
          {{ $t('assetMapSolum.allPlaced') }}
        </p>
        <ul v-else class="amx-gw-list">
          <li
            v-for="gw in availableGateways"
            :key="gw.gw_mac"
            class="amx-gw-item"
            draggable="true"
            @dragstart="onGwDragStart($event, gw)"
          >
            <span class="amx-gw-dot" :class="Number(gw.active) === 0 ? 'on' : 'off'"></span>
            <span class="amx-gw-name">{{ gw.devicename || gw.devicelocation || gw.gw_mac.slice(-6) }}</span>
            <span v-if="gwHumidity(gw.gw_mac) !== null" class="amx-gw-hum" :title="gwTitle(gw.gw_mac)"><span class="material-symbols-outlined amx-hum-icon">humidity_percentage</span>{{ gwHumidity(gw.gw_mac).toFixed(0) }}%</span>
            <span class="amx-gw-count">{{ gw.tag_count }}</span>
          </li>
        </ul>
        <p class="amx-side-foot">
          {{ $t('assetMapSolum.dragHint') }}
          <span v-if="excludedSet.size > 0" class="amx-side-excluded">
            {{ $t('assetMapSolum.placedOnOtherFloors', { n: excludedSet.size }) }}
          </span>
        </p>
      </aside>

      <aside v-else-if="!mini && selectedPlacement" class="amx-sidebar amx-sidebar-popup">
        <div class="amx-popup-header">
          <div class="amx-popup-title">
            <span class="amx-gw-dot" :class="isConnected(selectedPlacement.gw_mac) ? 'on' : 'off'"></span>
            <span class="amx-popup-name">{{ gwName(selectedPlacement.gw_mac) }}</span>
          </div>
          <button class="amx-popup-close" @click="selectedPlacement = null">×</button>
        </div>
        <p class="amx-popup-mac">{{ selectedPlacement.gw_mac }}</p>
        <div v-if="popupLoading" class="amx-popup-state">{{ $t('assetMapSolum.popupLoading') }}</div>
        <div v-else-if="popupTags.length === 0" class="amx-popup-state">{{ $t('assetMapSolum.noLinkedTags') }}</div>
        <ul v-else class="amx-tag-list">
          <li
            v-for="t in popupTags"
            :key="t.tag_mac"
            class="amx-tag-item"
            role="button"
            tabindex="0"
            :title="$t('assetMapSolum.tagDetailTitle', { name: t.devicename || t.tag_mac })"
            @click="goToTagList(t)"
            @keydown.enter="goToTagList(t)"
          >
            <div class="amx-tag-row1">
              <span class="amx-tag-name">{{ t.devicename || t.tag_mac.slice(-6) }}</span>
              <span class="amx-tag-loc">{{ t.taglocation || '-' }}</span>
            </div>
            <div class="amx-tag-row2">
              <span>🌡 {{ t.tag_temperature != null ? Number(t.tag_temperature).toFixed(1) + '°C' : '-' }}</span>
              <span>🔋 {{ t.tag_vol != null ? Number(t.tag_vol).toFixed(2) + 'V' : '-' }}</span>
              <span class="amx-tag-time">{{ formatTime(t.tag_sensingtime) }}</span>
            </div>
          </li>
        </ul>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const COLORS = ['emerald', 'amber', 'cyan', 'violet', 'rose', 'lime'];

export default {
  name: 'AssetMapSolum',
  props: {
    /** 맵 데이터: { id, name, image_path, image_width, image_height, placements: [...] } */
    map: { type: Object, default: null },
    /** 편집 권한 (관리자만 true) */
    canEdit: { type: Boolean, default: false },
    /** 미니 모드 (대시보드 위젯용 — 헤더/사이드바 숨김) */
    mini: { type: Boolean, default: false },
    /** 맵 최대 높이(px). 0 이면 제한 없음(가로 100%·원본 비율). 대시보드에서 세로가 긴 도면이
     *  아래 피드를 밀어내지 않도록 이 높이 안에 맞춰 축소(비율 유지, 가로 중앙 정렬). */
    maxHeight: { type: Number, default: 0 },
    /** 다른 맵에 이미 배치된 GW MAC 목록 — 사이드바에서 숨김 (중복 배치 방지) */
    excludedMacs: { type: Array, default: () => [] },
  },
  emits: ['saved'],
  data() {
    return {
      frameWidth: 0,        // maxHeight 모드에서 프레임 폭 (ResizeObserver 로 갱신)
      placements: [],
      isEditing: false,
      isDirty: false,
      saving: false,
      gateways: [],
      gatewaysFetchedFor: null,
      selectedPlacement: null,
      popupTags: [],
      popupLoading: false,
      gwPollId: null,
    };
  },
  computed: {
    availableColors() { return COLORS; },
    company() { return localStorage.getItem('company') || ''; },
    aspectRatio() {
      if (!this.map || !this.map.image_width) return 56.25;
      return (Number(this.map.image_height) / Number(this.map.image_width)) * 100;
    },
    /** 캔버스 박스 크기. maxHeight 지정 시 프레임 폭·최대 높이 안에 비율 유지로 맞춤. */
    canvasStyle() {
      if (!this.maxHeight || !this.frameWidth) return { paddingTop: this.aspectRatio + '%' };
      const ratio = this.aspectRatio / 100;                       // h / w
      const boxW = Math.min(this.frameWidth, this.maxHeight / ratio);
      return { width: boxW + 'px', height: Math.round(boxW * ratio) + 'px', margin: '0 auto' };
    },
    placedMacs() {
      return new Set(this.placements.map(p => p.gw_mac));
    },
    excludedSet() {
      return new Set(this.excludedMacs);
    },
    availableGateways() {
      return this.gateways.filter(gw =>
        !this.placedMacs.has(gw.gw_mac) && !this.excludedSet.has(gw.gw_mac)
      );
    },
    gatewaysByMac() {
      const m = {};
      for (const gw of this.gateways) m[gw.gw_mac] = gw;
      return m;
    },
  },
  watch: {
    'map.id': {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.placements = (this.map.placements || []).map(p => ({
          ...p,
          x_percent: Number(p.x_percent),
          y_percent: Number(p.y_percent),
          width_percent: Number(p.width_percent),
          height_percent: Number(p.height_percent),
        }));
        this.isDirty = false;
        this.selectedPlacement = null;
        this.fetchGateways();
      },
    },
  },
  mounted() {
    // maxHeight 모드: 프레임 폭 추적
    if (typeof ResizeObserver !== 'undefined' && this.$refs.frameEl) {
      this._ro = new ResizeObserver((es) => { const w = es[0]?.contentRect?.width; if (w) this.frameWidth = w; });
      this._ro.observe(this.$refs.frameEl);
    }
    if (this.map) this.fetchGateways();
    // 게이트웨이 상태 주기적 갱신 (10초)
    this.gwPollId = setInterval(() => this.fetchGateways(true), 10000);
  },
  beforeUnmount() {
    if (this._ro) { this._ro.disconnect(); this._ro = null; }
    if (this.gwPollId) clearInterval(this.gwPollId);
  },
  methods: {
    gwName(mac) {
      const gw = this.gatewaysByMac[mac];
      if (!gw) return mac ? mac.slice(-6) : '-';
      return gw.devicename || gw.devicelocation || mac.slice(-6);
    },
    tagCount(mac) {
      const gw = this.gatewaysByMac[mac];
      return gw ? Number(gw.tag_count || 0) : 0;
    },
    // 게이트웨이 센서 습도(%RH). 센서 없음/오류/10분 넘은 값은 null → 배지 숨김
    gwHumidity(mac) {
      const gw = this.gatewaysByMac[mac];
      if (!gw || gw.env_humidity == null || Number(gw.env_status) !== 0) return null;
      if (gw.env_measured_at && (Date.now() - new Date(gw.env_measured_at).getTime()) > 10 * 60 * 1000) return null;
      return Number(gw.env_humidity);
    },
    gwTitle(mac) {
      const h = this.gwHumidity(mac);
      if (h === null) return this.gwName(mac);
      const gw = this.gatewaysByMac[mac];
      const t = gw?.env_measured_at ? new Date(gw.env_measured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
      return `${this.gwName(mac)} · ${this.$t('assetMapSolum.humidity')} ${h.toFixed(1)} %RH${t ? ` (${this.$t('assetMapSolum.humidityMeasured')} ${t})` : ''}`;
    },
    isConnected(mac) {
      const gw = this.gatewaysByMac[mac];
      return gw ? Number(gw.active) === 0 : false;
    },
    placementStyle(p) {
      return {
        left: p.x_percent + '%',
        top: p.y_percent + '%',
        width: p.width_percent + '%',
        height: p.height_percent + '%',
      };
    },
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      if (isNaN(d.getTime())) return '';
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    },
    async fetchGateways(silent = false) {
      const company = this.company;
      if (!company) return;
      try {
        const r = await axios.get('/api/gatewayList', { params: { company, limit: 200 } });
        this.gateways = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        this.gatewaysFetchedFor = company;
      } catch (e) {
        if (!silent) console.error('AssetMapSolum: fetchGateways failed:', e);
      }
    },
    toggleEdit() {
      if (!this.canEdit) return;
      this.isEditing = !this.isEditing;
      this.selectedPlacement = null;
    },

    /* === 드래그&드롭 (사이드바 → 지도) === */
    onGwDragStart(e, gw) {
      e.dataTransfer.setData('text/gw_mac', gw.gw_mac);
      e.dataTransfer.effectAllowed = 'copy';
    },
    onDragOver(e) {
      if (!this.isEditing) return;
      e.dataTransfer.dropEffect = 'copy';
    },
    onDrop(e) {
      if (!this.isEditing) return;
      const gw_mac = e.dataTransfer.getData('text/gw_mac');
      if (!gw_mac) return;
      if (this.placedMacs.has(gw_mac)) return;
      const rect = this.$refs.containerEl.getBoundingClientRect();
      const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
      const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
      const w = 10, h = 8;
      this.placements.push({
        id: this._uuid(),
        gw_mac,
        x_percent: Math.max(0, Math.min(100 - w, xPercent - w / 2)),
        y_percent: Math.max(0, Math.min(100 - h, yPercent - h / 2)),
        width_percent: w,
        height_percent: h,
        color: 'emerald',
      });
      this.isDirty = true;
    },

    /* === 마커 이동 === */
    onPlacementMouseDown(e, p) {
      if (!this.isEditing) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = this.$refs.containerEl.getBoundingClientRect();
      const startX = e.clientX, startY = e.clientY;
      const startXP = Number(p.x_percent), startYP = Number(p.y_percent);
      const wP = Number(p.width_percent), hP = Number(p.height_percent);
      const onMove = (ev) => {
        const dx = ((ev.clientX - startX) / rect.width) * 100;
        const dy = ((ev.clientY - startY) / rect.height) * 100;
        p.x_percent = Math.max(0, Math.min(100 - wP, startXP + dx));
        p.y_percent = Math.max(0, Math.min(100 - hP, startYP + dy));
        this.isDirty = true;
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    startResize(e, p) {
      if (!this.isEditing) return;
      e.preventDefault();
      const rect = this.$refs.containerEl.getBoundingClientRect();
      const startX = e.clientX, startY = e.clientY;
      const startW = Number(p.width_percent), startH = Number(p.height_percent);
      const xP = Number(p.x_percent), yP = Number(p.y_percent);
      const onMove = (ev) => {
        const dw = ((ev.clientX - startX) / rect.width) * 100;
        const dh = ((ev.clientY - startY) / rect.height) * 100;
        p.width_percent = Math.max(3, Math.min(100 - xP, startW + dw));
        p.height_percent = Math.max(3, Math.min(100 - yP, startH + dh));
        this.isDirty = true;
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    removePlacement(p) {
      this.placements = this.placements.filter(x => x.id !== p.id);
      if (this.selectedPlacement && this.selectedPlacement.id === p.id) {
        this.selectedPlacement = null;
      }
      this.isDirty = true;
    },

    /* === 보기 모드: 마커 클릭 → 태그 팝업 === */
    onPlacementClick(p) {
      if (this.isEditing) return;
      if (this.mini) return;
      this.selectPlacement(p);
    },
    async selectPlacement(p) {
      this.selectedPlacement = p;
      this.popupTags = [];
      this.popupLoading = true;
      try {
        const r = await axios.get('/api/getTags', {
          params: { gw_mac_parent: p.gw_mac, company: this.company },
        });
        this.popupTags = Array.isArray(r.data) ? r.data : [];
      } catch (e) {
        console.error('AssetMapSolum: getTags failed', e);
        this.popupTags = [];
      } finally {
        this.popupLoading = false;
      }
    },
    /** 팝업의 태그 항목 클릭 → /tagList 로 이동 + filter 쿼리 자동 입력 */
    goToTagList(tag) {
      const filter = tag.devicename || tag.tag_mac;
      this.$router.push({ path: '/tagList', query: { filter } });
    },

    /* === 저장 === */
    async save() {
      if (!this.map?.id) return;
      this.saving = true;
      try {
        const body = {
          placements: this.placements.map(p => ({
            id: p.id,
            gw_mac: p.gw_mac,
            x_percent: Number(p.x_percent),
            y_percent: Number(p.y_percent),
            width_percent: Number(p.width_percent),
            height_percent: Number(p.height_percent),
            color: p.color || 'emerald',
          })),
        };
        await axios.put(`/api/asset-maps/${this.map.id}/placements`, body);
        this.isDirty = false;
        this.$emit('saved');
      } catch (e) {
        console.error('AssetMapSolum: save failed', e);
        alert(this.$t('assetMapSolum.saveFail', { msg: e.response?.data?.message || e.message }));
      } finally {
        this.saving = false;
      }
    },

    _uuid() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
      // fallback
      return 'p-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    },
  },
};
</script>

<style scoped>
/* ===== Empty state ===== */
.amx-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 220px; padding: 32px; gap: 8px;
  background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px;
  color: #64748b;
}
.amx-empty-icon { font-size: 36px; }
.amx-empty-text { margin: 0; font-size: 14px; }
.amx-empty-link { font-size: 13px; color: #1e73ff; text-decoration: none; }
.amx-empty-link:hover { text-decoration: underline; }

/* ===== Container ===== */
.amx { display: flex; flex-direction: column; gap: 12px; width: 100%; }
.amx-mini { gap: 0; }

/* ===== Header ===== */
.amx-header {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  flex-wrap: wrap;
}
.amx-title { display: flex; align-items: center; gap: 8px; }
.amx-name { font-size: 16px; font-weight: 700; color: #111827; }
.amx-dirty { font-size: 12px; color: #f59e0b; font-weight: 500; }
.amx-actions { display: flex; gap: 8px; }
.amx-btn {
  border: 1px solid #e5e7eb; background: #ffffff; color: #374151;
  padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 13px;
  transition: all 0.15s ease;
}
.amx-btn:hover { border-color: #1e73ff; color: #1e73ff; }
.amx-btn.is-on { background: #eff6ff; border-color: #1e73ff; color: #1e73ff; }
.amx-btn-primary { background: #1e73ff; border-color: #1e73ff; color: #ffffff; }
.amx-btn-primary:hover { background: #1d4ed8; color: #ffffff; }
.amx-btn-primary:disabled { background: #cbd5e1; border-color: #cbd5e1; cursor: not-allowed; }

/* ===== Body layout ===== */
.amx-body { display: flex; gap: 16px; align-items: flex-start; }
@media (max-width: 900px) { .amx-body { flex-direction: column; } }

/* ===== Map frame ===== */
.amx-map-frame {
  flex: 1; min-width: 0;
  background: #f1f5f9; border: 1px solid #e5e7eb; border-radius: 8px;
  overflow: hidden;
}
.amx-canvas { position: relative; width: 100%; }
.amx-img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: contain; user-select: none; pointer-events: none;
}

/* ===== Placement marker ===== */
.amx-placement {
  position: absolute; border: 2px solid; border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  user-select: none; transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.amx-editing .amx-placement { cursor: move; touch-action: none; }
.amx:not(.amx-editing) .amx-placement { cursor: pointer; }
.amx-placement:hover { box-shadow: 0 0 0 2px rgba(30, 115, 255, 0.25); }
.amx-placement-selected { box-shadow: 0 0 0 3px rgba(30, 115, 255, 0.5); }

.amx-placement-label {
  position: absolute; top: -18px; left: 0;
  font-size: 11px; font-weight: 600; color: #111827;
  background: rgba(255,255,255,0.95); padding: 1px 6px; border-radius: 3px;
  max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
.amx-placement-count {
  font-size: 14px; font-weight: 700; color: #111827;
  background: rgba(255,255,255,0.85); padding: 2px 8px; border-radius: 10px;
  pointer-events: none;
}
.amx-placement-status {
  position: absolute; top: 4px; left: 4px;
  width: 8px; height: 8px; border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.9);
}
.amx-placement-status.on { background: #10b981; }
.amx-placement-status.off { background: #ef4444; }
/* 습도 배지: 박스 안 하단 중앙, 진한 파랑 알약 + 흰 글씨 (박스 밖 작은 배지는 이웃 라벨과 겹치고 시인성이 낮았음) */
.amx-placement-hum {
  position: absolute; bottom: 6px; left: 50%; transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 15px; font-weight: 800; color: #fff; letter-spacing: 0.01em;
  background: #1d4ed8; padding: 3px 12px 3px 8px; border-radius: 999px;
  white-space: nowrap; box-shadow: 0 2px 6px rgba(29,78,216,0.45), 0 0 0 2px rgba(255,255,255,0.9); pointer-events: none;
  max-width: calc(100% - 8px); overflow: hidden;
}
.amx-placement-hum .amx-hum-icon { color: #fff; font-size: 17px; }
.amx-hum-icon { font-size: 13px; line-height: 1; color: #2563eb; }
.amx-gw-hum { display: inline-flex; align-items: center; gap: 1px; font-size: 11px; font-weight: 700; color: #1d4ed8; background: #eff6ff; padding: 1px 6px 1px 4px; border-radius: 8px; }

.amx-placement-remove {
  position: absolute; top: -8px; right: -8px;
  width: 20px; height: 20px; border-radius: 50%; border: none;
  background: #ef4444; color: white; font-size: 16px; line-height: 1;
  cursor: pointer; opacity: 0; transition: opacity 0.15s ease;
  display: flex; align-items: center; justify-content: center;
}
.amx-placement:hover .amx-placement-remove { opacity: 1; }

.amx-placement-resize {
  position: absolute; right: 0; bottom: 0;
  width: 12px; height: 12px;
  background: rgba(255,255,255,0.7);
  border-top-left-radius: 3px;
  cursor: se-resize; opacity: 0.5;
}
.amx-placement-resize:hover { opacity: 1; }

/* color picker */
.amx-color-picker {
  position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%);
  display: flex; gap: 3px; opacity: 0; transition: opacity 0.15s ease;
  pointer-events: none;
}
.amx-placement:hover .amx-color-picker { opacity: 1; pointer-events: auto; }
.amx-color-dot {
  width: 12px; height: 12px; border-radius: 50%; border: none; cursor: pointer;
  transition: transform 0.1s ease;
}
.amx-color-dot:hover { transform: scale(1.25); }
.amx-color-dot.is-active { box-shadow: 0 0 0 2px white, 0 0 0 3px rgba(0,0,0,0.4); }

/* color palette */
.amx-color-emerald { border-color: #34d399; background: rgba(52, 211, 153, 0.25); }
.amx-color-amber   { border-color: #f59e0b; background: rgba(245, 158, 11, 0.25); }
.amx-color-cyan    { border-color: #22d3ee; background: rgba(34, 211, 238, 0.25); }
.amx-color-violet  { border-color: #a78bfa; background: rgba(167, 139, 250, 0.25); }
.amx-color-rose    { border-color: #fb7185; background: rgba(251, 113, 133, 0.25); }
.amx-color-lime    { border-color: #a3e635; background: rgba(163, 230, 53, 0.25); }
.amx-color-bg-emerald { background: #34d399; }
.amx-color-bg-amber   { background: #f59e0b; }
.amx-color-bg-cyan    { background: #22d3ee; }
.amx-color-bg-violet  { background: #a78bfa; }
.amx-color-bg-rose    { background: #fb7185; }
.amx-color-bg-lime    { background: #a3e635; }

/* connected = full opacity, disconnected = dimmed */
.amx-placement:not(.amx-placement-connected) { opacity: 0.6; }

/* ===== Sidebar ===== */
.amx-sidebar {
  flex: 0 0 260px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 12px; max-height: 70vh; overflow-y: auto;
}
.amx-side-title { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #374151; }
.amx-side-hint { font-size: 12px; color: #9ca3af; text-align: center; padding: 16px 0; margin: 0; }
.amx-side-foot { margin-top: 12px; font-size: 11px; color: #6b7280; text-align: center; line-height: 1.5; }
.amx-side-excluded { display: inline-block; margin-top: 2px; color: #9ca3af; }

.amx-gw-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
.amx-gw-item {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 8px; border: 1px solid #e5e7eb; border-radius: 6px;
  background: #ffffff; cursor: grab; user-select: none;
  transition: background 0.1s ease;
}
.amx-gw-item:hover { background: #f9fafb; }
.amx-gw-item:active { cursor: grabbing; }
.amx-gw-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.amx-gw-dot.on { background: #10b981; }
.amx-gw-dot.off { background: #ef4444; }
.amx-gw-name { flex: 1; font-size: 13px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.amx-gw-count { font-size: 11px; color: #6b7280; background: #f3f4f6; padding: 1px 6px; border-radius: 8px; }

/* ===== Popup (view mode) ===== */
.amx-sidebar-popup { padding: 0; }
.amx-popup-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-bottom: 1px solid #e5e7eb;
}
.amx-popup-title { display: flex; align-items: center; gap: 6px; min-width: 0; }
.amx-popup-name { font-size: 13px; font-weight: 600; color: #111827; }
.amx-popup-mac { padding: 4px 12px; margin: 0; font-family: ui-monospace, monospace; font-size: 11px; color: #6b7280; }
.amx-popup-close { background: none; border: none; cursor: pointer; font-size: 18px; color: #6b7280; }
.amx-popup-state { padding: 16px; text-align: center; font-size: 12px; color: #6b7280; }
.amx-tag-list { list-style: none; padding: 8px 12px; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.amx-tag-item {
  padding: 6px 8px; border: 1px solid #f1f5f9; border-radius: 4px;
  background: #f9fafb; cursor: pointer; outline: none;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
}
.amx-tag-item:hover, .amx-tag-item:focus-visible {
  border-color: #93c5fd;
  background: #eff6ff;
  transform: translateX(2px);
}
.amx-tag-item:focus-visible { box-shadow: 0 0 0 2px rgba(30, 115, 255, 0.35); }
.amx-tag-row1 { display: flex; justify-content: space-between; gap: 6px; font-size: 12px; }
.amx-tag-name { color: #111827; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.amx-tag-loc { color: #6b7280; flex: 0 0 auto; }
.amx-tag-row2 { display: flex; gap: 8px; margin-top: 2px; font-size: 11px; color: #6b7280; }
.amx-tag-time { margin-left: auto; }
</style>
